import React, { useEffect, useRef } from "react";

/**
 * AdSenseSlot — single, non-intrusive AdSense ad unit.
 *
 * UX rules (per integration playbook):
 *  - Reserves a fixed min-height to PREVENT Cumulative-Layout-Shift (CLS)
 *    when the creative loads. Container never collapses even if the ad
 *    fails to fill — keeps the page layout calm.
 *  - Skips render entirely when running inside the Capacitor Android
 *    WebView (Google policy forbids AdSense inside native apps; AdMob
 *    handles that surface instead).
 *  - Renders a tiny "Sponsored" label above the slot so users can tell
 *    paid placements apart from editorial content (FTC / ASCI best
 *    practice — same policy we already follow for affiliate Sponsored
 *    Picks).
 *
 * Configuration (all client-safe, public-by-design):
 *  - client  : AdSense publisher (e.g. "ca-pub-3264817702143284")
 *  - slot    : the per-unit ID created in AdSense → Ads → By ad unit
 *  - format  : "auto" (responsive — default), or a fixed shape
 *  - layout  : "in-feed" | "in-article" | undefined (banner default)
 *  - minHeightClass : Tailwind class for the reserved CLS height. Tune
 *                     per placement; the default 250px matches a desktop
 *                     billboard and a mobile rectangle gracefully.
 *
 * If `slot` is empty (i.e. the user hasn't pasted their AdSense slot ID
 * into REACT_APP_ADSENSE_SLOT_HOME_MID yet) the component renders the
 * reserved space invisibly so the layout doesn't shift the day the slot
 * gets filled in. This keeps Adukoo running cleanly while the AdSense
 * approval/review is in flight.
 */
export default function AdSenseSlot({
    client = process.env.REACT_APP_ADSENSE_CLIENT || "",
    slot = "",
    format = "auto",
    layout,
    layoutKey,
    fullWidthResponsive = true,
    minHeightClass = "min-h-[120px] sm:min-h-[200px]",
    className = "",
    testId = "adsense-slot",
}) {
    const insRef = useRef(null);
    const pushedRef = useRef(false);

    // Native-app detection — same guard the loader script uses. Memo via
    // useRef so we only compute it once per mount.
    const isNativeApp = (() => {
        if (typeof window === "undefined") return false;
        const w = window;
        if (w.Capacitor && (w.Capacitor.isNativePlatform === true || (typeof w.Capacitor.isNativePlatform === "function" && w.Capacitor.isNativePlatform()))) return true;
        return /wv|; wv\)/.test(navigator.userAgent || "");
    })();

    useEffect(() => {
        if (isNativeApp) return;
        if (!client || !slot) return; // not configured yet — keep reserved space
        if (pushedRef.current) return;
        // adsbygoogle.push() must be called AFTER the <ins> element is in
        // the DOM. The loader script in index.html attaches `adsbygoogle`
        // to window; we retry once if it hasn't loaded yet.
        const tryPush = () => {
            try {
                // eslint-disable-next-line no-undef
                (window.adsbygoogle = window.adsbygoogle || []).push({});
                pushedRef.current = true;
            } catch {
                // ignore — happens during HMR / strict-mode double-mount
            }
        };
        if (window.adsbygoogle) {
            tryPush();
        } else {
            const t = setTimeout(tryPush, 1200);
            return () => clearTimeout(t);
        }
    }, [client, slot, isNativeApp]);

    // Hide entirely inside the native app — AdMob owns that surface.
    if (isNativeApp) return null;

    // v78.44 — Return null when slot ID isn't configured yet.
    //
    // Previous behaviour rendered an invisible reserved-height placeholder
    // to absorb the eventual ad's CLS. Founder rejected that: AdSense was
    // just approved, AdMob is enabled, but several home/browse positions
    // still don't have a `slot` ID pasted into .env, and the empty gray
    // blocks were eating screen real-estate without serving anything.
    //
    // New rule: no slot ID → no DOM, no reserved space, no layout
    // commitment. The day the publisher pastes the slot ID into
    // REACT_APP_ADSENSE_SLOT_* the component will render normally and
    // surrounding content will reflow — that's an acceptable one-time
    // shift in exchange for never showing an empty placeholder.
    if (!client || !slot) return null;

    return (
        <div className={`w-full ${className}`} data-testid={testId}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-aduku-navy/50 mb-1 text-center">
                Sponsored
            </div>
            <div className={`w-full ${minHeightClass} flex items-center justify-center bg-white border border-aduku-border/40 rounded-lg overflow-hidden`}>
                <ins
                    ref={insRef}
                    className="adsbygoogle"
                    style={{ display: "block", width: "100%", height: "100%" }}
                    data-ad-client={client}
                    data-ad-slot={slot}
                    data-ad-format={format}
                    data-ad-layout={layout}
                    data-ad-layout-key={layoutKey}
                    data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
                />
            </div>
        </div>
    );
}
